import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { useNavigate } from 'react-router-dom';
import { getPermissionMatrix, GetPermissionMatrixOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, Download, Check, X } from 'lucide-react';
import { isOwnerOrAdmin } from '@/lib/roles';

type RolePerm = GetPermissionMatrixOutputType['matrix'][0];

export default function PermissionMatrix() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState<RolePerm[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (!isOwnerOrAdmin(user.role)) { navigate('/'); return; }
    getPermissionMatrix({}).then(d => setMatrix(d.matrix)).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  const exportCsv = () => {
    const headers = ['Role', 'Description', 'Can Edit Staff', 'Can View Sensitive HR', 'Can Approve Leave', 'Can Export Data', 'Access Areas'];
    const rows = matrix.map(r => [r.role, r.description, r.canEditOtherStaff ? 'Yes' : 'No', r.canViewSensitiveHR ? 'Yes' : 'No', r.canApproveLeave ? 'Yes' : 'No', r.canExportData ? 'Yes' : 'No', r.areas.join('; ')]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'permission-matrix.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;

  const BoolCell = ({ val }: { val: boolean }) => val ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-muted-foreground" />;

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Role Permission Matrix</h1>
            <p className="text-sm text-muted-foreground">What each role can access across the Staff Hub</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
      </div>

      {/* Summary table */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Permission Summary</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-medium">Role</th>
                <th className="text-center py-2 px-3 font-medium">Edit Staff</th>
                <th className="text-center py-2 px-3 font-medium">View HR</th>
                <th className="text-center py-2 px-3 font-medium">Approve Leave</th>
                <th className="text-center py-2 px-3 font-medium">Export</th>
                <th className="text-center py-2 px-3 font-medium">Areas</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map(r => (
                <tr key={r.role} className="border-b border-border/50">
                  <td className="py-2.5 px-3 font-medium">{r.role}</td>
                  <td className="py-2.5 px-3 text-center"><BoolCell val={r.canEditOtherStaff} /></td>
                  <td className="py-2.5 px-3 text-center"><BoolCell val={r.canViewSensitiveHR} /></td>
                  <td className="py-2.5 px-3 text-center"><BoolCell val={r.canApproveLeave} /></td>
                  <td className="py-2.5 px-3 text-center"><BoolCell val={r.canExportData} /></td>
                  <td className="py-2.5 px-3 text-center"><Badge variant="secondary">{r.areas.length}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detailed role cards */}
      <div className="grid gap-4">
        {matrix.map(r => (
          <Card key={r.role}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                {r.role}
                {r.canViewSensitiveHR && <Badge variant="destructive" className="text-xs">Sensitive Access</Badge>}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{r.description}</p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-1.5">
                {r.areas.map(a => (
                  <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
